// Customer-side card payment modal (Stripe Elements).
//
// Uses the publishable key from GET /api/stripe/config and the clientSecret
// from the EXISTING, untouched POST /api/customer/stripe/payment-intent. The
// card is collected + confirmed client-side via Stripe.js; on success the
// caller records it through POST /api/customer/payments/confirm. NO commission
// / Stripe Connect logic lives here — that's all server-side in the unchanged
// endpoints.

import { api } from "@tkos/api-client";

// Minimal structural types for the slice of Stripe.js we use (avoids a hard
// dependency on @stripe/stripe-js types).
interface StripeCardElement {
  mount(target: string | HTMLElement): void;
  unmount(): void;
  on(event: string, handler: (e: { error?: { message?: string } }) => void): void;
}
interface StripeElements { create(type: "card", opts?: Record<string, unknown>): StripeCardElement; }
interface StripeConfirmResult {
  paymentIntent?: { id: string; status: string };
  error?: { message?: string };
}
interface StripeInstance {
  elements(opts?: Record<string, unknown>): StripeElements;
  confirmCardPayment(clientSecret: string, data: Record<string, unknown>): Promise<StripeConfirmResult>;
}
type StripeFactory = (key: string) => StripeInstance;

let stripePromise: Promise<StripeInstance | null> | null = null;

function loadStripeJs(): Promise<StripeFactory | null> {
  return new Promise((resolve) => {
    const w = window as unknown as { Stripe?: StripeFactory };
    if (w.Stripe) { resolve(w.Stripe); return; }
    const done = () => resolve((window as unknown as { Stripe?: StripeFactory }).Stripe ?? null);
    const existing = document.querySelector<HTMLScriptElement>("script[data-stripe-js]");
    if (existing) {
      existing.addEventListener("load", done);
      existing.addEventListener("error", () => resolve(null));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.async = true;
    s.dataset["stripeJs"] = "1";
    s.addEventListener("load", done);
    s.addEventListener("error", () => resolve(null));
    document.head.appendChild(s);
  });
}

async function getStripe(): Promise<StripeInstance | null> {
  if (!stripePromise) {
    stripePromise = (async () => {
      const factory = await loadStripeJs();
      if (!factory) return null;
      let pk = "";
      try {
        const cfg = await api<{ publishableKey?: string }>("/stripe/config");
        pk = cfg?.publishableKey || "";
      } catch { /* no key → no card payments */ }
      return pk ? factory(pk) : null;
    })();
  }
  return stripePromise;
}

export interface StripePayResult {
  ok: boolean;
  paymentIntentId?: string;
  error?: string;
}

/** Open the card-entry modal and confirm payment for `clientSecret`. */
export async function openStripePayModal(opts: {
  clientSecret: string;
  amountLabel: string;
  description: string;
}): Promise<StripePayResult> {
  const stripe = await getStripe();
  if (!stripe) return { ok: false, error: "Card payments aren't available right now — please contact your retailer." };

  return new Promise<StripePayResult>((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "cust-paymodal-overlay";
    overlay.innerHTML = `
      <div class="cust-paymodal" role="dialog" aria-modal="true" aria-label="Pay by card">
        <div class="cust-paymodal__head">
          <div class="cust-paymodal__title">Pay ${opts.amountLabel}</div>
          <button type="button" class="cust-paymodal__x" data-role="close" aria-label="Close">&times;</button>
        </div>
        <p class="cust-paymodal__desc"></p>
        <div class="cust-paymodal__card" data-role="card"></div>
        <div class="cust-paymodal__err" data-role="err" hidden></div>
        <button type="button" class="cust-paymodal__pay" data-role="pay">Pay ${opts.amountLabel}</button>
        <div class="cust-paymodal__secure">🔒 Secure payment processed by Stripe</div>
      </div>`;
    const descEl = overlay.querySelector<HTMLElement>(".cust-paymodal__desc");
    if (descEl) descEl.textContent = opts.description;
    document.body.appendChild(overlay);

    const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
    const errEl = q<HTMLElement>("[data-role=err]");
    const payBtn = q<HTMLButtonElement>("[data-role=pay]");

    const elements = stripe.elements();
    const card = elements.create("card", { style: { base: { fontSize: "15px", color: "#1e293b" } } });
    card.mount(q<HTMLElement>("[data-role=card]"));
    card.on("change", (e) => {
      if (e.error && e.error.message) { errEl.textContent = e.error.message; errEl.hidden = false; }
      else errEl.hidden = true;
    });

    let settled = false;
    const close = (result: StripePayResult): void => {
      if (settled) return;
      settled = true;
      try { card.unmount(); } catch { /* ignore */ }
      overlay.remove();
      document.removeEventListener("keydown", onKey);
      resolve(result);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close({ ok: false }); };

    q<HTMLButtonElement>("[data-role=close]").addEventListener("click", () => close({ ok: false }));
    overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close({ ok: false }); });
    document.addEventListener("keydown", onKey);

    payBtn.addEventListener("click", () => {
      payBtn.disabled = true;
      payBtn.textContent = "Processing…";
      errEl.hidden = true;
      void (async () => {
        try {
          const res = await stripe.confirmCardPayment(opts.clientSecret, { payment_method: { card } });
          if (res.error) {
            errEl.textContent = res.error.message || "Payment failed. Please try again.";
            errEl.hidden = false;
            payBtn.disabled = false;
            payBtn.textContent = `Pay ${opts.amountLabel}`;
            return;
          }
          if (res.paymentIntent && res.paymentIntent.status === "succeeded") {
            close({ ok: true, paymentIntentId: res.paymentIntent.id });
          } else {
            errEl.textContent = "Payment not completed. Please try again.";
            errEl.hidden = false;
            payBtn.disabled = false;
            payBtn.textContent = `Pay ${opts.amountLabel}`;
          }
        } catch {
          errEl.textContent = "Payment failed. Please try again.";
          errEl.hidden = false;
          payBtn.disabled = false;
          payBtn.textContent = `Pay ${opts.amountLabel}`;
        }
      })();
    });
  });
}
